/**
 * WAV (RIFF/WAVE) metadata and **lossless** trimming (§20).
 *
 * WAV is the one media format where trimming is unambiguously lossless: the
 * samples are stored uncompressed, so cutting a time range is copying a byte
 * range of the `data` chunk and rewriting two size fields. No decoder, no
 * re-encoder, no patent-encumbered codec — which is exactly the trimming §20
 * permits ("재인코딩 없는 스트림 복사(무손실)만").
 *
 * The parser is a chunk walker rather than a fixed-offset reader, because the
 * `fmt ` and `data` chunks are not required to be adjacent or in a fixed place:
 * a `LIST`/`INFO` block or a `fact` chunk can sit between them, and a reader
 * that assumed `data` at offset 44 would read metadata as audio. Every chunk
 * except the one being trimmed is preserved byte for byte, so titles and
 * cue points survive a trim (§13, §24).
 */
import { HandlerError } from '../contract.js';

export interface WavFormat {
  /** 1 = PCM, 3 = IEEE float, 0xFFFE = extensible; others are compressed. */
  readonly audioFormat: number;
  readonly channels: number;
  readonly sampleRate: number;
  readonly bitsPerSample: number;
  /** Bytes per second of audio; the trim converts seconds through this. */
  readonly byteRate: number;
  /** Bytes per sample frame (all channels); a trim must stay aligned to it. */
  readonly blockAlign: number;
}

export interface WavInfo {
  readonly format: WavFormat;
  readonly durationSec: number;
  /** Byte offset and length of the `data` chunk's payload. */
  readonly dataOffset: number;
  readonly dataSize: number;
  /** RIFF INFO tags (title, artist, …) when present. */
  readonly tags: Readonly<Record<string, string>>;
  /** True when the stream is PCM or float — the only kinds this trims. */
  readonly losslessTrimmable: boolean;
}

interface Chunk { readonly id: string; readonly start: number; readonly size: number }

function readChunks(bytes: Uint8Array): { chunks: Chunk[]; view: DataView } {
  if (bytes.length < 12) throw new HandlerError('error.damagedOrIncomplete', 'file is too small to be WAV');
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (ascii(bytes, 0, 4) !== 'RIFF' || ascii(bytes, 8, 4) !== 'WAVE') {
    throw new HandlerError('error.damagedOrIncomplete', 'not a RIFF/WAVE file');
  }

  const chunks: Chunk[] = [];
  let pos = 12;
  while (pos + 8 <= bytes.length) {
    const id = ascii(bytes, pos, 4);
    const size = view.getUint32(pos + 4, true);
    const start = pos + 8;
    if (start + size > bytes.length) {
      // A truncated final chunk: record what is actually present rather than
      // trusting a size that runs off the end (§34).
      chunks.push({ id, start, size: bytes.length - start });
      break;
    }
    chunks.push({ id, start, size });
    // Chunks are word-aligned: an odd size is followed by a pad byte.
    pos = start + size + (size & 1);
  }
  return { chunks, view };
}

export function parseWav(bytes: Uint8Array): WavInfo {
  const { chunks, view } = readChunks(bytes);
  const fmt = chunks.find((c) => c.id === 'fmt ');
  const data = chunks.find((c) => c.id === 'data');
  if (!fmt || !data) throw new HandlerError('error.damagedOrIncomplete', 'WAV is missing its fmt or data chunk');

  const format: WavFormat = {
    audioFormat: view.getUint16(fmt.start, true),
    channels: view.getUint16(fmt.start + 2, true),
    sampleRate: view.getUint32(fmt.start + 4, true),
    byteRate: view.getUint32(fmt.start + 8, true),
    blockAlign: view.getUint16(fmt.start + 12, true),
    bitsPerSample: view.getUint16(fmt.start + 14, true),
  };

  const byteRate = format.byteRate || (format.sampleRate * format.blockAlign) || 1;
  const durationSec = data.size / byteRate;

  // The extensible format wraps a real one in a subformat GUID; the first two
  // bytes of that GUID are the actual format tag.
  const realFormat = format.audioFormat === 0xfffe && fmt.size >= 26
    ? view.getUint16(fmt.start + 24, true)
    : format.audioFormat;

  return {
    format,
    durationSec,
    dataOffset: data.start,
    dataSize: data.size,
    tags: readInfoTags(bytes, chunks),
    losslessTrimmable: realFormat === 1 || realFormat === 3,
  };
}

/** RIFF `LIST`/`INFO` tags, the closest WAV has to ID3. */
function readInfoTags(bytes: Uint8Array, chunks: readonly Chunk[]): Record<string, string> {
  const list = chunks.find((c) => c.id === 'LIST' && ascii(bytes, c.start, 4) === 'INFO');
  if (!list) return {};
  const labels: Record<string, string> = {
    INAM: 'title', IART: 'artist', IPRD: 'album', ICRD: 'date', IGNR: 'genre', ICMT: 'comment',
  };
  const tags: Record<string, string> = {};
  let pos = list.start + 4;
  const end = list.start + list.size;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  while (pos + 8 <= end) {
    const id = ascii(bytes, pos, 4);
    const size = view.getUint32(pos + 4, true);
    const label = labels[id];
    if (label) tags[label] = ascii(bytes, pos + 8, size).replace(/\0+$/, '').trim();
    pos += 8 + size + (size & 1);
  }
  return tags;
}

/**
 * Cut the audio to `[startSec, endSec]`, losslessly.
 *
 * The cut points are snapped to whole sample frames — a cut inside a frame
 * would split a sample across channels and produce a click — and the `data`
 * chunk's payload is replaced with the copied byte range while every other
 * chunk, in order, is kept exactly as it was.
 */
export function trimWav(bytes: Uint8Array, startSec: number, endSec: number): Uint8Array {
  const info = parseWav(bytes);
  if (!info.losslessTrimmable) {
    throw new HandlerError('error.featuresUnsupported', 'only PCM and float WAV can be trimmed losslessly');
  }
  const { blockAlign } = info.format;
  const align = blockAlign > 0 ? blockAlign : 1;

  const clampSec = (s: number): number => Math.max(0, Math.min(info.durationSec, s));
  const start = clampSec(startSec);
  const end = clampSec(endSec);
  if (end <= start) throw new HandlerError('error.unableToRead', 'the trim range is empty');

  const byteRate = info.format.byteRate || info.format.sampleRate * align;
  // Snap to frame boundaries, and never past the data chunk.
  const from = info.dataOffset + Math.min(info.dataSize, alignDown(Math.round(start * byteRate), align));
  const to = info.dataOffset + Math.min(info.dataSize, alignDown(Math.round(end * byteRate), align));
  const newDataSize = to - from;

  const { chunks } = readChunks(bytes);
  const parts: Uint8Array[] = [];
  // "RIFF", placeholder size, "WAVE" — rewritten once the length is known.
  const header = bytes.slice(0, 12);
  parts.push(header);

  for (const chunk of chunks) {
    const head = new Uint8Array(8);
    const hv = new DataView(head.buffer);
    writeAscii(head, 0, chunk.id);
    if (chunk.id === 'data') {
      hv.setUint32(4, newDataSize, true);
      parts.push(head, bytes.slice(from, to));
      if (newDataSize & 1) parts.push(new Uint8Array(1)); // pad byte
    } else {
      hv.setUint32(4, chunk.size, true);
      const padded = chunk.size + (chunk.size & 1);
      parts.push(head, bytes.slice(chunk.start, chunk.start + padded));
    }
  }

  const out = concat(parts);
  // RIFF size counts everything after the first 8 bytes.
  new DataView(out.buffer).setUint32(4, out.length - 8, true);
  return out;
}

/* ------------------------------------------------------------------ *
 * helpers
 * ------------------------------------------------------------------ */

function alignDown(value: number, align: number): number {
  return value - (value % align);
}

function ascii(bytes: Uint8Array, offset: number, length: number): string {
  let s = '';
  for (let i = 0; i < length && offset + i < bytes.length; i++) s += String.fromCharCode(bytes[offset + i]!);
  return s;
}

function writeAscii(bytes: Uint8Array, offset: number, text: string): void {
  for (let i = 0; i < text.length; i++) bytes[offset + i] = text.charCodeAt(i) & 0xff;
}

function concat(parts: readonly Uint8Array[]): Uint8Array {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let pos = 0;
  for (const p of parts) { out.set(p, pos); pos += p.length; }
  return out;
}
